import { useQuery } from '@tanstack/react-query';
import type { LbOptions } from '@uefacom-newdev/liveblog-integration-react';
import Liveblog from '@uefacom-newdev/liveblog-integration-react';
import { useMemo, lazy, memo } from 'react';

import Container from './Container';
import ShowNewPosts from './ShowNewPosts';
import { getLiveblogData, D3JsonPollingService } from './utils';

import { getEditorialByIdQuery } from '@queries/editorial';
import { language, IS_DEBUG } from '@utilities/browser';
import { getRefreshRate, PollingTimes } from '@utilities/live';

const Composer = lazy(() => import(/* webpackPrefetch: true */ './Composer'));
const UefaEditorial = lazy(() => import(/* webpackPrefetch: true */ './UefaEditorial'));
const MatchEvents = lazy(() => import(/* webpackPrefetch: true */ './MatchEvents'));
const MatchSummary = lazy(() => import(/* webpackPrefetch: true */ './MatchSummary'));
const FreePhoto = lazy(() => import(/* webpackPrefetch: true */ './FreePhoto'));
const Social = lazy(() => import(/* webpackPrefetch: true */ './Social'));
const Poll = lazy(() => import(/* webpackPrefetch: true */ './Poll'));
const VideoPost = lazy(() => import(/* webpackPrefetch: true */ './VideoPost'));
const Card = lazy(() => import(/* webpackPrefetch: true */ './Card'));
const PromoApp = lazy(() => import(/* webpackPrefetch: true */ './PromoApp'));

const defaults = {
  refreshRate: 2000,
  language,
  debug: IS_DEBUG,
  autostart: true,
  urls: {
    posts: '{blogId}/posts',
    events: '{blogId}/updates',
  },
  headers: window.cobaltApiKey
    ? {
        ['x-api-key']: window.cobaltApiKey,
      }
    : {},
  showJsonLD: true,
  useLoadingSkeletonPosts: true,
} as const;

const customComponents = {
  showNewPosts: ShowNewPosts,
  container: Container,
  Composer,
  UefaEditorial,
  MatchSummary,
  MatchEvents,
  Social,
  FreePhoto,
  Poll,
  VideoPost,
  Card,
} as const;

const postFilter = (data) => {
  const lbPost = data.post ?? data;
  const lbPostType = lbPost?.attributes?.liveblogPostData?.lbPostType;
  if (['MATCH_INFO', 'FORM_GUIDE', 'HEAD_TO_HEAD', 'LINEUP'].includes(lbPostType)) {
    return !window.matchMedia(`(min-width: 960px)`).matches;
  }

  return true;
};

const UefaComLb = ({
  options,
  options: { match, draw },
}: {
  options: LbOptions & {
    useJsonDocuments?: boolean;
    lbData?: any;
    disableTeamLink?: boolean;
    disablePlayerLink?: boolean;
  };
}) => {
  const blogHeadline = useMemo(
    () =>
      match
        ? `${match.homeTeam?.displayName}-${match.awayTeam?.displayName} - ${match.competition?.displayName} - ${match.round?.seasonYear}`
        : draw?.title,
    [match, draw],
  );

  const blogDescription = useMemo(
    () =>
      match
        ? `${match.competition?.displayName} - ${match.round?.displayName} - Phase: ${match.round?.phase} - ${new Date(
            match.kickOffTime?.dateTime,
          )}`
        : draw?.title,
    [match, draw],
  );

  const date = useMemo(() => (match ? match.kickOffTime?.dateTime : draw?.date), [match, draw]);

  const { data: liveblogData } = useQuery({
    ...getEditorialByIdQuery(options.blogId),
    select: (response) => getLiveblogData(response),
    placeholderData: options.useJsonDocuments ? options.lbData : null,
    enabled: !options.useJsonDocuments && !!options.blogId,
  });

  const lbOptions = useMemo(
    () => ({
      ...defaults,
      ...options,
      date,
      baseUefaImgUrl: options.baseUrl,
      baseUrl: options.siteBaseUrl,
      blogHeadline,
      blogDescription,
      liveblogData,
      transport: {
        pollingService: options.useJsonDocuments ? new D3JsonPollingService(match) : null,
        filter: (post) => postFilter(post),
      },
      refreshRate: match ? getRefreshRate(match) : PollingTimes.LIVE,
      useSkeletonPosts: !options.enableAppPromo,
      customComponents: {
        ...customComponents,
        ...(options.enableAppPromo && {
          emptyMessage: PromoApp,
        }),
      },
    }),
    [blogDescription, blogHeadline, date, liveblogData, match, options],
  );

  if (!liveblogData) return null;

  return <Liveblog options={lbOptions} />;
};

export default memo(UefaComLb);
